/**
 * URL redaction for the dashboard's analytics sinks (D18): PostHog
 * `before_send`, Vercel Analytics `beforeSend`, and the GA mount decision in
 * the Centered layout.
 *
 * Several unauthenticated dashboard pages are entered with a BEARER capability
 * in the URL:
 *
 *   /reset-password?code=, /[slug]/reset-password?code=
 *       password reset AND teammate invites (utils/teammate-invite.js)
 *   /magic-login?loginToken=                    magic link
 *   /connect/[provider]/redirect?access_token=&id_token=   Google/Microsoft
 *   /redirect?code=&state=, /[slug]/redirect?code=&state=   Shippo OAuth
 *
 * Anyone holding that URL can act on it, so it must never reach PostHog, Vercel
 * or GA. Only the secret values change; every other param stays.
 *
 * ☠️ `code` is NOT a secret everywhere here: /create-account?code=IWF26 and
 * /onboarding?code= are live coupon links whose attribution must survive. So
 * `code` is masked only on the reset / OAuth-redirect routes (CODE_ROUTE_RE).
 *
 * ⚠️ Known residual, accepted (DECISIONS D18): posthog-js persists the FIRST
 * page URL as `$initial_person_info` before `before_send` runs and sends it in
 * every `/flags` request's person_properties. That request bypasses
 * `before_send`.
 */

const REDACTED = '[redacted]';

// Canonical (lower-case, alphanumerics only) param names that always carry a
// capability, plus anything containing token / secret / password. `code` is
// handled separately (route-scoped, see above).
const SECRET_KEYS = new Set([
  'token',
  'logintoken',
  'accesstoken',
  'idtoken',
  'refreshtoken',
  'oauthtoken',
  'confirmation',
  'preview',
  'registrationtoken',
  'secret',
  'clientsecret',
  'signature',
  'sig',
  'password',
  'apikey',
]);
const SECRET_SUBSTRINGS = ['token', 'secret', 'password'];

// Pathnames (real URLs, not route patterns) where `?code=` is a capability.
const CODE_ROUTE_RE =
  /^(?:\/[^/?#]+)?\/(?:reset-password|redirect)\/?$|^\/connect\/[^/?#]+\/redirect\/?$/;

/**
 * Route PATTERNS (router.pathname) that are entered with a capability in the
 * URL. GA (unconsented, @next/third-parties) is not mounted on these.
 */
export const TOKEN_BEARING_ROUTES = [
  '/reset-password',
  '/[slug]/reset-password',
  '/magic-login',
  '/connect/[provider]/redirect',
  '/redirect',
  '/[slug]/redirect',
  '/email-confirmation',
];

export function isTokenBearingRoute(pathname) {
  return TOKEN_BEARING_ROUTES.includes(pathname);
}

function canonicalKey(k) {
  return String(k).toLowerCase().replace(/[^a-z0-9]/g, '');
}

function safeDecode(s) {
  try {
    return decodeURIComponent(String(s).replace(/\+/g, ' '));
  } catch (e) {
    return String(s);
  }
}

/**
 * True when a param NAME carries a capability. `code` only counts when the
 * caller says the URL is a reset / OAuth-redirect route.
 */
export function isSecretParam(key, { maskCode = false } = {}) {
  const canonical = canonicalKey(safeDecode(key));
  if (!canonical) return false;
  if (canonical === 'code') return maskCode;
  if (SECRET_KEYS.has(canonical)) return true;
  return SECRET_SUBSTRINGS.some((s) => canonical.includes(s));
}

/** Is `pathname` a route where `?code=` is a capability? */
export function isCodeRoute(pathname) {
  return typeof pathname === 'string' && CODE_ROUTE_RE.test(pathname);
}

function redactParams(raw, opts) {
  if (typeof raw !== 'string' || raw === '') return raw;
  return raw
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return pair;
      const rawKey = pair.slice(0, eq);
      return isSecretParam(rawKey, opts) ? `${rawKey}=${REDACTED}` : pair;
    })
    .join('&');
}

const TEXT_QUERY_RE = /([?&#;])([^=&#\s"'<>?;]+)=([^&#\s"'<>]*)/g;
const TEXT_CODE_RE =
  /(\/(?:reset-password|redirect)\/?[?#](?:[^#\s"'<>]*?&)?code=)([^&#\s"'<>]+)/g;

// Capability URLs for OTHER surfaces that the dashboard still renders as page
// text: the freshly minted design-share link (PresentationView shows it once,
// in plaintext, for copying — a `$copy_autocapture` `$selected_content` or an
// `$el_text` would carry it) and payment links. Same patterns as
// embed-engine's redactUrl.js. A minted share token is 43-char base64url
// (share-token.js); dashboard routes never have one after `/design/`.
const SHARE_TOKEN_TEXT_RE = /\/design\/[A-Za-z0-9_-]{43}(?![A-Za-z0-9_-])/g;
const PAY_TOKEN_TEXT_RE = /\/api\/pay\/[^/?#\s"'<>]+/g;

function redactCapabilityPaths(str) {
  return str
    .replace(SHARE_TOKEN_TEXT_RE, '/design/:token')
    .replace(PAY_TOKEN_TEXT_RE, '/api/pay/:token');
}

/** Free-text pass for strings not known to be a single URL. */
export function redactText(str) {
  if (typeof str !== 'string' || str === '') return str;
  return redactCapabilityPaths(str)
    .replace(TEXT_QUERY_RE, (match, sep, rawKey, value) => {
      if (!value || value === REDACTED) return match;
      return isSecretParam(rawKey) ? `${sep}${rawKey}=${REDACTED}` : match;
    })
    .replace(TEXT_CODE_RE, (match, prefix, value) =>
      value === REDACTED ? match : `${prefix}${REDACTED}`
    );
}

/**
 * Redact one URL — absolute or relative. Returns the same shape it was given.
 */
export function redactUrl(url) {
  if (typeof url !== 'string' || url === '') return url;
  try {
    const hashIdx = url.indexOf('#');
    const beforeHash = hashIdx === -1 ? url : url.slice(0, hashIdx);
    const hash = hashIdx === -1 ? null : url.slice(hashIdx + 1);
    const qIdx = beforeHash.indexOf('?');
    const base = qIdx === -1 ? beforeHash : beforeHash.slice(0, qIdx);
    const query = qIdx === -1 ? null : beforeHash.slice(qIdx + 1);

    // Classify on a normalized pathname: drop a scheme or protocol-relative
    // authority (`//host/reset-password`) and root a bare relative path
    // (`reset-password`), or the code route is missed and `?code=` leaks.
    const m = /^((?:[a-z][a-z0-9+.-]*:)?\/\/[^/]*)(.*)$/i.exec(base);
    const rawPath = m ? m[2] : base;
    const path = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    const opts = { maskCode: isCodeRoute(path) };

    // An `<a href>` to a share / pay link (autocapture attr__href).
    let out = redactCapabilityPaths(base);
    if (query !== null) out += `?${redactParams(query, opts)}`;
    if (hash !== null) out += `#${hash.includes('=') ? redactParams(hash, opts) : hash}`;
    return out;
  } catch (e) {
    return redactText(url);
  }
}

const URL_KEY_RE = /(url|pathname|referrer|href)$/i;
const LOOKS_LIKE_URL_RE = /^(?:[a-z][a-z0-9+.-]*:\/\/|\/)/i;
const ELEMENTS_CHAIN_HREF_RE = /(href=")([^"]*)(")/g;
const MAX_DEPTH = 8;

function scrubString(key, value) {
  if (key === '$elements_chain') {
    return redactText(
      value.replace(ELEMENTS_CHAIN_HREF_RE, (m, a, href, b) => `${a}${redactUrl(href)}${b}`)
    );
  }
  if (typeof key === 'string' && URL_KEY_RE.test(key)) return redactUrl(value);
  return LOOKS_LIKE_URL_RE.test(value) ? redactText(redactUrl(value)) : redactText(value);
}

function scrubKey(key) {
  return LOOKS_LIKE_URL_RE.test(key) ? redactText(redactUrl(key)) : redactText(key);
}

function scrubValue(key, value, depth, seen) {
  if (typeof value === 'string') return scrubString(key, value);
  if (value === null || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH) return '[scrubbed]';
  if (seen.has(value)) return '[circular]';
  seen.add(value);
  let out;
  if (Array.isArray(value)) {
    out = value.map((v) => scrubValue(key, v, depth + 1, seen));
  } else {
    out = {};
    for (const k of Object.keys(value)) {
      const nextKey = scrubKey(k);
      const nextValue = scrubValue(k, value[k], depth + 1, seen);
      // Two keys can redact to the same one ($heatmap_data keyed by two
      // reset URLs with different codes): merge the arrays instead of letting
      // the later entry silently replace the earlier one.
      if (
        Object.prototype.hasOwnProperty.call(out, nextKey) &&
        Array.isArray(out[nextKey]) &&
        Array.isArray(nextValue)
      ) {
        out[nextKey] = out[nextKey].concat(nextValue);
      } else {
        out[nextKey] = nextValue;
      }
    }
  }
  seen.delete(value);
  return out;
}

/**
 * posthog-js `before_send`: walks properties / $set / $set_once and rewrites
 * every URL-bearing value and key ($current_url, $pathname, $referrer,
 * $initial_*, $session_entry_*, autocapture hrefs, heatmap keys, web vitals).
 *
 * Never throws — posthog-js rethrows into the capture() caller. On any failure
 * the event is DROPPED (returns null).
 */
export function scrubPostHogEvent(data) {
  try {
    if (!data || typeof data !== 'object') return data;
    const out = { ...data };
    for (const field of ['properties', '$set', '$set_once']) {
      if (out[field] && typeof out[field] === 'object') {
        out[field] = scrubValue(field, out[field], 0, new WeakSet());
      }
    }
    return out;
  } catch (e) {
    return null;
  }
}

/** @vercel/analytics `beforeSend`: `{ type, url }` with the URL redacted. */
export function redactVercelEvent(event) {
  try {
    if (!event || typeof event.url !== 'string') return event;
    return { ...event, url: redactUrl(event.url) };
  } catch (e) {
    return null;
  }
}
